import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Zap, Clock, Volume2, Leaf, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import heroVideo1 from "@/assets/hero-video.mp4";
import heroVideo2 from "@/assets/hero-video-2.mp4";
import heroVideo3 from "@/assets/hero-video-3.mp4";
import heroVideo4 from "@/assets/hero-video-4.mov";

const heroVideos = [heroVideo1, heroVideo2, heroVideo3, heroVideo4];

const features = [
  { icon: Zap, label: "Smart", color: "text-sky-blue" },
  { icon: Clock, label: "Fast", color: "text-purple" },
  { icon: Volume2, label: "Noiseless", color: "text-amber-glow" },
  { icon: Leaf, label: "Green", color: "text-eco-green" },
];

const brandNames = [
  { name: "Sustainable Development LLC", arabic: "تطوير الاستدامة" },
  { name: "تطوير الاستدامة", arabic: "Sustainable Development LLC", isArabic: true },
];

const Hero = () => {
  const [brandIndex, setBrandIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsFading(true);
      setTimeout(() => {
        setBrandIndex((prev) => (prev + 1) % brandNames.length);
        setIsFading(false);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const [videoIndex, setVideoIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoEnd = useCallback(() => {
    setFadeIn(false);
    setTimeout(() => {
      setVideoIndex((prev) => (prev + 1) % heroVideos.length);
      setFadeIn(true);
    }, 600);
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Video */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          key={videoIndex}
          autoPlay
          muted
          playsInline
          onEnded={handleVideoEnd}
          className={`w-full h-full object-cover transition-opacity duration-700 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
        >
          <source src={heroVideos[videoIndex]} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/70 to-background" />
      </div>

      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-eco-green/5 via-transparent to-sky-blue/5 animate-gradient-shift" style={{ backgroundSize: "200% 200%" }} />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 rounded-full ${i % 2 === 0 ? 'bg-eco-green/30' : 'bg-sky-blue/30'}`}
            style={{
              left: `${10 + i * 12}%`,
              top: `${20 + (i % 3) * 25}%`,
              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 pt-24">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-block mb-6 animate-fade-in opacity-0" style={{ animationDelay: "0.1s" }}>
            <span 
              className={`px-4 py-2 rounded-full bg-gradient-to-r from-eco-green/20 via-primary/20 to-sky-blue/20 text-primary text-sm font-semibold border border-primary/20 inline-block transition-all duration-300 ${
                brandNames[brandIndex].isArabic ? 'font-arabic' : ''
              } ${isFading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
            >
              {brandNames[brandIndex].name}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-bold mb-4 animate-fade-in opacity-0" style={{ animationDelay: "0.2s" }}>
            House Of{" "}
            <span className="bg-gradient-to-r from-primary via-amber-glow to-primary bg-clip-text text-transparent">Mobility</span>
          </h1>

          {/* Features */}
          <div className="flex flex-wrap gap-4 mb-8 animate-fade-in opacity-0" style={{ animationDelay: "0.3s" }}>
            {features.map((feature, index) => (
              <div key={feature.label} className="flex items-center gap-2 text-muted-foreground group">
                <div className={`p-1.5 rounded-lg bg-secondary/50 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`w-5 h-5 ${feature.color}`} />
                </div>
                <span className="font-medium">{feature.label}</span>
                {index < features.length - 1 && (
                  <span className="ml-2 text-muted-foreground/50">•</span>
                )}
              </div>
            ))}
          </div>

          {/* Subtitle */}
          <h2 className="text-2xl md:text-3xl font-semibold mb-4 animate-fade-in opacity-0" style={{ animationDelay: "0.4s" }}>
            E-Transportation
          </h2>

          {/* Description */}
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl animate-fade-in opacity-0 leading-relaxed" style={{ animationDelay: "0.5s" }}>
            We chose sustainability because we operate in an eco-conscious manner and believe that every strategy, product, and service can leave a green footprint. Our sustainable mobility solutions are aligned with Vision 2030.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 animate-fade-in opacity-0" style={{ animationDelay: "0.6s" }}>
            <Button 
              size="xl"
              asChild
              className="bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light hover:from-eco-green-light hover:via-teal-bright hover:to-eco-green text-white font-semibold shadow-lg shadow-eco-green/25 hover:shadow-xl hover:shadow-eco-green/30 transition-all duration-300 hover:scale-105"
            >
              <Link to="/services">
                Explore Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <Button variant="heroOutline" size="xl" asChild>
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-fade-in opacity-0" style={{ animationDelay: "0.8s" }}>
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-gradient-to-b from-eco-green to-primary rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
